import { ApiClient } from '@api/api-configuration';

/**
 * Hand this browser's push token back on the way out.
 *
 * A push token belongs to the BROWSER PROFILE, not the person, so it outlives the
 * session that registered it. Left active, the next notification for the
 * logged-out user is still delivered to a browser nobody is signed into, and the
 * row stays theirs until somebody else signs in and claims it.
 *
 * Deactivating rather than deleting because there is no delete endpoint, and
 * because `is_active` is exactly what the send path filters on
 * (`active_tokens = [t for t in tokens if t.is_active]`). Signing back in
 * re-registers with `isActive` defaulting true, so this is reversible by the
 * ordinary flow.
 *
 * This does NOT replace the server-side claim
 * (`register_notification_token`): a tab can be closed, a session can expire,
 * and a second account can sign in without the first ever logging out. It makes
 * the hand-over rare rather than routine.
 */

/** Logging out must not hang on a notification call. */
const DEACTIVATE_TIMEOUT_MS = 1500;

const withTimeout = <T>(work: Promise<T>, ms: number): Promise<T | void> =>
  Promise.race([work, new Promise<void>((resolve) => setTimeout(resolve, ms))]);

const currentToken = async (): Promise<string | null> => {
  // Asked BEFORE Firebase is touched: `getToken` prompts when permission is
  // "default", and a permission prompt on the way out is absurd.
  if (typeof Notification === 'undefined' || Notification.permission !== 'granted') return null;

  // Imported lazily so the auth layer, which every page loads, does not pull
  // Firebase into its module graph. `WebPushSetup` calls `initializeApp` and
  // `getMessaging` at module scope, and `getMessaging` throws outright in a
  // browser without push support.
  const [{ getToken }, { messaging, VAPID_KEY }] = await Promise.all([
    import('firebase/messaging'),
    import('./WebPushSetup'),
  ]);
  return (await getToken(messaging, { vapidKey: VAPID_KEY })) || null;
};

/**
 * Best effort, and deliberately silent: a failure here must never keep somebody
 * signed in. Bounded by {@link DEACTIVATE_TIMEOUT_MS} so a hung request delays
 * the redirect by at most that long.
 */
export const deactivatePushToken = async (): Promise<void> => {
  try {
    await withTimeout(
      (async () => {
        const deviceToken = await currentToken();
        if (!deviceToken) return;
        await ApiClient.notificationsApi.notifications.registerNotificationToken({
          deviceToken,
          platform: 'web',
          isActive: false,
        });
      })(),
      DEACTIVATE_TIMEOUT_MS
    );
  } catch {
    // Intentionally swallowed. See the doc comment.
  }
};

export default deactivatePushToken;
